/**
 * ModuleDetailPage — Individual module content
 *
 * Dynamically loads module data by moduleId, renders content blocks
 * via ModuleContentRenderer, and tracks completion.
 * Includes reading progress bar, section navigation, and back-to-top.
 */

import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle,
  ChevronUp,
  List,
  X,
} from 'lucide-react';

import { useProgress } from '../context/ProgressContext';
import { useGamification } from '../context/GamificationContext';
import { getCharacterForModule } from '../data/characters';
import ModuleContentRenderer from '../components/module/ModuleContentRenderer';
import CharacterAvatar from '../components/characters/CharacterAvatar';
import Tondo from '../components/ornament/Tondo';
import EveningPlate from '../components/art/EveningPlate';
import Button from '../components/shared/Button';
import Card from '../components/shared/Card';
import { getModuleById } from '../data/modules/index';
import { recordModuleComplete } from '../services/analytics';

const TOTAL_MODULES = 8;

// --- Reading Progress Bar ---
const ReadingProgressBar = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-1 bg-courtyard-stone/10">
      <motion.div
        className="h-full bg-gradient-to-r from-courtyard-terracotta to-courtyard-gold"
        style={{ width: `${progress}%` }}
        transition={{ duration: 0.1 }}
      />
    </div>
  );
};

// --- Back to Top Button ---
const BackToTopButton = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-40 w-11 h-11 rounded-full bg-courtyard-stone/80 text-courtyard-parchment shadow-lg hover:bg-courtyard-stone transition-colors flex items-center justify-center"
          aria-label="Back to top"
        >
          <ChevronUp className="w-5 h-5" aria-hidden="true" />
        </motion.button>
      )}
    </AnimatePresence>
  );
};

// --- Section Table of Contents ---
const SectionNav = ({ sections, onSelect }) => {
  const [open, setOpen] = useState(false);

  if (sections.length === 0) return null;

  const handleSelect = (id) => {
    setOpen(false);
    onSelect(id);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-courtyard-stone/5 border border-courtyard-stone/10 text-sm font-medium text-courtyard-stone/80 hover:bg-courtyard-stone/10 transition-colors"
      >
        <List className="w-4 h-4" aria-hidden="true" />
        Sections
        {open && <X className="w-3.5 h-3.5 ml-1" aria-hidden="true" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 w-[calc(100vw-2rem)] sm:w-72 max-h-80 overflow-y-auto bg-white rounded-xl border border-courtyard-gold/20 shadow-lg z-30"
          >
            <nav className="py-2">
              {sections.map((section, i) => (
                <button
                  key={section.id}
                  onClick={() => handleSelect(section.id)}
                  className="w-full text-left px-4 py-2.5 text-sm text-courtyard-stone/80 hover:bg-courtyard-gold/10 hover:text-courtyard-stone transition-colors flex gap-3 items-baseline"
                >
                  <span className="text-xs font-bold text-courtyard-gold/60 flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="leading-snug">{section.title}</span>
                </button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- Main Page ---
const ModuleDetailPage = () => {
  const { moduleId } = useParams();
  const {
    moduleProgress,
    markModuleStarted,
    markModuleCompleted,
    getAchievementContext,
  } = useProgress();
  const { addXP, checkAchievements } = useGamification();
  const contentRef = useRef(null);

  const numericId = parseInt(moduleId, 10);
  const [moduleData, setModuleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const charInfo = useMemo(
    () => getCharacterForModule(numericId),
    [numericId]
  );

  const status = moduleProgress[numericId]?.status || 'not_started';
  const isCompleted = status === 'completed';

  // Dynamically import module data and merge with metadata
  useEffect(() => {
    setLoading(true);
    setError(null);

    const padded = String(numericId).padStart(2, '0');
    import(`../data/modules/module${padded}.js`)
      .then((mod) => {
        const meta = getModuleById(numericId);
        const contentKey = `MODULE_${padded}_CONTENT`;
        const exercisesKey = `MODULE_${padded}_EXERCISES`;
        const summaryKey = `MODULE_${padded}_SUMMARY`;

        setModuleData({
          title: meta?.title || `Evening ${numericId}`,
          subtitle: meta?.subtitle || '',
          blocks: mod[contentKey] || [],
          exercises: mod[exercisesKey] || [],
          summary: mod[summaryKey] || null,
        });
        setLoading(false);
      })
      .catch(() => {
        setError('That evening could not be found.');
        setLoading(false);
      });
  }, [numericId]);

  // Mark as started on mount
  useEffect(() => {
    if (numericId >= 1 && numericId <= TOTAL_MODULES) {
      markModuleStarted(numericId);
    }
  }, [numericId, markModuleStarted]);

  const handleComplete = () => {
    markModuleCompleted(numericId);
    addXP(100, 'Evening finished');
    recordModuleComplete(numericId); // a no-op unless the reader said yes to usage statistics
    // State updates are async, so include the module just completed by hand
    const base = getAchievementContext();
    const completedIds = base.completedIds.includes(numericId)
      ? base.completedIds
      : [...base.completedIds, numericId];
    checkAchievements({
      ...base,
      completedIds,
      modulesCompleted: completedIds.length,
    });
  };

  // Build final blocks list
  const allBlocks = useMemo(() => {
    if (!moduleData) return [];
    const blocks = moduleData.blocks || [];
    const hasInlineExercises = blocks.some((b) => b.type === 'exercise');
    const hasInlineSummary = blocks.some((b) => b.type === 'summary');
    return [
      ...blocks,
      ...(!hasInlineExercises && moduleData.exercises
        ? moduleData.exercises.map((ex, i) => ({
            type: 'exercise',
            number: ex.number || i + 1,
            title: ex.title,
            instructions: ex.description || ex.instructions,
            prompts: ex.prompts || [],
          }))
        : []),
      ...(!hasInlineSummary && moduleData.summary
        ? [{
            type: 'summary',
            points: moduleData.summary.takeaways || moduleData.summary.keyTakeaways || moduleData.summary.points || [],
            nextPreview: moduleData.summary.nextPreview,
            characterId: charInfo?.primary?.id,
          }]
        : []),
    ];
  }, [moduleData, charInfo]);

  // Extract heading blocks for section nav
  const sections = useMemo(() => {
    return allBlocks
      .map((block, index) => {
        if (block.type === 'heading' || block.type === 'subheading') {
          return { id: `section-${index}`, title: block.text, index };
        }
        if (block.type === 'exercise') {
          return { id: `section-${index}`, title: `Exercise ${block.number}: ${block.title}`, index };
        }
        if (block.type === 'summary') {
          return { id: `section-${index}`, title: 'Key Takeaways', index };
        }
        return null;
      })
      .filter(Boolean);
  }, [allBlocks]);

  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(sectionId);
    if (el) {
      const offset = 24;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }, []);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center text-courtyard-stone/80">
        Loading this evening...
      </div>
    );
  }

  if (error || !moduleData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-courtyard-stone/80 mb-4">
          {error || 'This evening is not ready yet.'}
        </p>
        <Link to="/evenings">
          <Button variant="secondary">Back to the Evenings</Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{`Evening ${numericId}: ${moduleData.title || ''} | Finding Your Voice`}</title>
      </Helmet>

      <ReadingProgressBar />
      <BackToTopButton />

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-8" ref={contentRef}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link
            to="/evenings"
            className="inline-flex items-center gap-1 text-sm text-courtyard-stone/80 hover:text-courtyard-stone mb-4"
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" /> All Evenings
          </Link>

          <div className="flex items-center gap-4">
            {charInfo?.primary && (
              <CharacterAvatar characterId={charInfo.primary.id} size="lg" />
            )}
            {charInfo?.supporting?.map((s) => (
              <CharacterAvatar key={s.id} characterId={s.id} size="sm" />
            ))}
            <Tondo evening={numericId} size={88} className="ml-auto flex-shrink-0" />
          </div>

          <h1 className="font-display text-3xl font-bold text-courtyard-stone mt-4">
            Evening {numericId}: {moduleData.title}
          </h1>
          {moduleData.subtitle && (
            <p className="text-courtyard-stone/80 mt-1">{moduleData.subtitle}</p>
          )}

          <EveningPlate evening={numericId} className="mt-6" />

          {/* Section Navigation */}
          {sections.length > 0 && (
            <div className="mt-5">
              <SectionNav sections={sections} onSelect={scrollToSection} />
            </div>
          )}
        </motion.div>

        {/* Content Blocks */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <ModuleContentRenderer blocks={allBlocks} />
        </motion.div>

        {/* Completion */}
        <motion.div
          className="pt-6 border-t border-courtyard-gold/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {isCompleted ? (
            <Card className="p-5 text-center bg-courtyard-olive/5 border-courtyard-olive/20">
              <CheckCircle className="w-8 h-8 text-courtyard-olive-dark mx-auto mb-2" aria-hidden="true" />
              <p className="font-bold text-courtyard-stone">Evening Finished</p>
              <p className="text-sm text-courtyard-stone/80 mt-1">
                You earned 100 XP for this one.
              </p>
            </Card>
          ) : (
            <div className="text-center">
              <Button variant="primary" size="lg" onClick={handleComplete}>
                Mark as Complete
              </Button>
            </div>
          )}
        </motion.div>

        {/* Navigation */}
        <div className="flex justify-between pt-4">
          {numericId > 1 ? (
            <Link to={`/evenings/${numericId - 1}`}>
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" /> Previous Evening
              </Button>
            </Link>
          ) : (
            <div />
          )}
          {numericId < TOTAL_MODULES ? (
            <Link to={`/evenings/${numericId + 1}`}>
              <Button variant="ghost" size="sm">
                Next Evening <ArrowRight className="w-4 h-4 ml-1" aria-hidden="true" />
              </Button>
            </Link>
          ) : (
            <div />
          )}
        </div>
      </div>
    </>
  );
};

export default ModuleDetailPage;
